import { useState, useRef, useEffect, useCallback } from 'react';
import { patientViewPath } from '../fetcher';
import { readDocumentNavigation, clearDocumentNavigation } from './documentNavigationSession';

// Session storage key for tracking if document was opened from a task
const DOCUMENT_FROM_TASK_KEY = 'documentFromTask';

// Session storage key for tracking if document was opened from a workflow
const DOCUMENT_FROM_WORKFLOW_KEY = 'documentFromWorkflow';

// Session storage key for tracking if document was opened from a patient view
const DOCUMENT_FROM_PATIENT_KEY = 'documentFromPatient';

// Session storage key for tracking if document was opened from a consultation
const DOCUMENT_FROM_CONSULTATION_KEY = 'documentFromConsultation';

/**
 * Prev/next through the navigation list, auto-advance after filing, and the "opened from" context
 * (task, workflow, patient, consultation) that decides where Back goes.
 */
const useDocumentNavigation = ({
  documentId,
  location,
  navigate,
  backPatientId,
  showWorkflowLogsModal,
  workflowPreviewModalOpen,
  selectedWorkflow,
  loadKey,
}) => {
  const [navigationContext, setNavigationContext] = useState(null);
  const [fromTaskId, setFromTaskId] = useState(null);
  const [fromWorkflowId, setFromWorkflowId] = useState(null);
  const [fromPatientNhsId, setFromPatientNhsId] = useState(null);
  const [fromPatientId, setFromPatientId] = useState(null);
  const [fromConsultationId, setFromConsultationId] = useState(null);

  // Auto-navigate to next (or prev at end of list) when current doc transitions QUEUED_FOR_FILING → FILED_AWAY
  // Only for the user who filed the document away
  const [shouldAutoNavigate, setShouldAutoNavigate] = useState(false);
  const [prevButtonAnimating, setPrevButtonAnimating] = useState(false);
  const [nextButtonAnimating, setNextButtonAnimating] = useState(false);

  // Read at click time so prev/next carry the workflow modals over without re-creating the handlers.
  const workflowCarryOverRef = useRef({ showWorkflowLogsModal, workflowPreviewModalOpen, selectedWorkflow });
  useEffect(() => {
    workflowCarryOverRef.current = { showWorkflowLogsModal, workflowPreviewModalOpen, selectedWorkflow };
  }, [showWorkflowLogsModal, workflowPreviewModalOpen, selectedWorkflow]);

  useEffect(() => {
    setShouldAutoNavigate(false);
    setPrevButtonAnimating(false);
    setNextButtonAnimating(false);
  }, [loadKey]);

  // Read/recover task context from location state or session storage on mount and when documentId changes
  useEffect(() => {
    try {
      const taskIdFromState = location.state?.taskId;

      if (taskIdFromState) {
        console.log('Document opened from task:', taskIdFromState);
        setFromTaskId(taskIdFromState);
        sessionStorage.setItem(DOCUMENT_FROM_TASK_KEY, taskIdFromState);
      } else {
        const taskIdFromStorage = sessionStorage.getItem(DOCUMENT_FROM_TASK_KEY);
        if (taskIdFromStorage) {
          console.log('Recovered task context from session storage:', taskIdFromStorage);
          setFromTaskId(taskIdFromStorage);
        } else {
          setFromTaskId(null);
        }
      }
    } catch (error) {
      console.warn('Failed to read/recover task context:', error);
      setFromTaskId(null);
    }
  }, [documentId, location.state]);

  // Read/recover workflow context from location state or session storage on mount and when documentId changes
  useEffect(() => {
    try {
      const workflowIdFromState = location.state?.fromWorkflowId;

      if (workflowIdFromState) {
        console.log('Document opened from workflow:', workflowIdFromState);
        setFromWorkflowId(workflowIdFromState);
        sessionStorage.setItem(DOCUMENT_FROM_WORKFLOW_KEY, workflowIdFromState);
      } else {
        const workflowIdFromStorage = sessionStorage.getItem(DOCUMENT_FROM_WORKFLOW_KEY);
        if (workflowIdFromStorage) {
          console.log('Recovered workflow context from session storage:', workflowIdFromStorage);
          setFromWorkflowId(workflowIdFromStorage);
        } else {
          setFromWorkflowId(null);
        }
      }
    } catch (error) {
      console.warn('Failed to read/recover workflow context:', error);
      setFromWorkflowId(null);
    }
  }, [documentId, location.state]);

  // Read/recover patient context from location state or session storage on mount and when documentId changes
  useEffect(() => {
    try {
      const patientContextFromState = location.state?.fromPatient;
      const patientNhsIdFromState = location.state?.patientNhsId;
      const patientIdFromState = location.state?.patientId;
      const patientNameFromState = location.state?.patientName;

      if (patientContextFromState && patientNhsIdFromState) {
        console.log('Document opened from patient:', patientNhsIdFromState);
        setFromPatientNhsId(patientNhsIdFromState);
        setFromPatientId(patientIdFromState || null);
        sessionStorage.setItem(DOCUMENT_FROM_PATIENT_KEY, JSON.stringify({
          nhsId: patientNhsIdFromState,
          patientId: patientIdFromState || null,
          name: patientNameFromState
        }));
      } else {
        const patientContextFromStorage = sessionStorage.getItem(DOCUMENT_FROM_PATIENT_KEY);
        if (patientContextFromStorage) {
          try {
            const { nhsId, patientId } = JSON.parse(patientContextFromStorage);
            console.log('Recovered patient context from session storage:', nhsId);
            setFromPatientNhsId(nhsId);
            setFromPatientId(patientId || null);
          } catch (parseError) {
            console.warn('Failed to parse patient context from session storage:', parseError);
            setFromPatientNhsId(null);
            setFromPatientId(null);
          }
        } else {
          setFromPatientNhsId(null);
          setFromPatientId(null);
        }
      }
    } catch (error) {
      console.warn('Failed to read/recover patient context:', error);
      setFromPatientNhsId(null);
      setFromPatientId(null);
    }
  }, [documentId, location.state]);

  // Read/recover consultation context from location state or session storage on mount and when documentId changes
  useEffect(() => {
    try {
      const consultationContextFromState = location.state?.fromConsultation;
      const consultationIdFromState = location.state?.consultationId;

      if (consultationContextFromState && consultationIdFromState) {
        console.log('Document opened from consultation:', consultationIdFromState);
        setFromConsultationId(consultationIdFromState);
        sessionStorage.setItem(DOCUMENT_FROM_CONSULTATION_KEY, consultationIdFromState);
      } else {
        const consultationIdFromStorage = sessionStorage.getItem(DOCUMENT_FROM_CONSULTATION_KEY);
        if (consultationIdFromStorage) {
          console.log('Recovered consultation context from session storage:', consultationIdFromStorage);
          setFromConsultationId(consultationIdFromStorage);
        } else {
          setFromConsultationId(null);
        }
      }
    } catch (error) {
      console.warn('Failed to read/recover consultation context:', error);
      setFromConsultationId(null);
    }
  }, [documentId, location.state]);

  // Read navigation context from session storage on mount and when documentId changes
  useEffect(() => {
    setNavigationContext(readDocumentNavigation(documentId));
  }, [documentId]);

  const navigateToIndex = useCallback((newIndex, options) => {
    const targetDocumentId = navigationContext.documentIds[newIndex];

    // Preserve workflow modal states across documents
    const { showWorkflowLogsModal: logsOpen, workflowPreviewModalOpen: previewOpen, selectedWorkflow: workflow } = workflowCarryOverRef.current;
    const navState = {};
    if (logsOpen) {
      navState.openWorkflowLogs = true;
      if (previewOpen && workflow) {
        navState.previewRuleId = workflow.ruleId;
      }
    }
    if (options.autoShowDetailsTab) {
      navState.autoShowDetailsTab = true;
    }

    navigate(`/document-detail/${targetDocumentId}`, { state: navState });
  }, [navigationContext, navigate]);

  const handleNavigatePrev = useCallback((options = {}) => {
    if (!navigationContext || navigationContext.currentIndex <= 0) return;
    navigateToIndex(navigationContext.currentIndex - 1, options);
  }, [navigationContext, navigateToIndex]);

  const handleNavigateNext = useCallback((options = {}) => {
    if (!navigationContext || navigationContext.currentIndex >= navigationContext.documentIds.length - 1) return;
    navigateToIndex(navigationContext.currentIndex + 1, options);
  }, [navigationContext, navigateToIndex]);

  /** Called when the current user's filing of this document completes. */
  const requestAutoNavigate = useCallback(() => setShouldAutoNavigate(true), []);

  // When QUEUED_FOR_FILING → FILED_AWAY for the filing user, animate Next (or Prev at end of list)
  // then navigate. Runs with a fresh navigationContext rather than one captured by the update stream.
  useEffect(() => {
    if (!shouldAutoNavigate) return;

    const canNavigateNext = navigationContext && navigationContext.currentIndex < navigationContext.documentIds.length - 1;
    const canNavigatePrev = navigationContext && navigationContext.currentIndex > 0;

    if (canNavigateNext) {
      setNextButtonAnimating(true);
      const timer = setTimeout(() => {
        setNextButtonAnimating(false);
        setShouldAutoNavigate(false);
        handleNavigateNext({ autoShowDetailsTab: true });
      }, 1000);
      return () => clearTimeout(timer);
    }

    if (canNavigatePrev) {
      setPrevButtonAnimating(true);
      const timer = setTimeout(() => {
        setPrevButtonAnimating(false);
        setShouldAutoNavigate(false);
        handleNavigatePrev({ autoShowDetailsTab: true });
      }, 1000);
      return () => clearTimeout(timer);
    }

    setShouldAutoNavigate(false);
  }, [shouldAutoNavigate, navigationContext, handleNavigateNext, handleNavigatePrev]);

  const handleBackToDocumentsList = useCallback(() => {
    clearDocumentNavigation();

    if (fromWorkflowId) {
      // Navigate back to workflow detail with logs open
      try {
        sessionStorage.removeItem(DOCUMENT_FROM_WORKFLOW_KEY);
      } catch (error) {
        console.warn('Failed to clear workflow context from session storage:', error);
      }

      navigate(`/workflow/${fromWorkflowId}`, {
        state: {
          openWorkflowLogs: true
        }
      });
    } else if (fromTaskId) {
      // Navigate back to tasks tab with taskId
      try {
        sessionStorage.removeItem(DOCUMENT_FROM_TASK_KEY);
      } catch (error) {
        console.warn('Failed to clear task context from session storage:', error);
      }

      navigate('/', {
        state: {
          selectTab: 'tasks',
          taskId: fromTaskId
        }
      });
    } else if (fromPatientNhsId) {
      try {
        sessionStorage.removeItem(DOCUMENT_FROM_PATIENT_KEY);
      } catch (error) {
        console.warn('Failed to clear patient context from session storage:', error);
      }

      const patientIdForBack = fromPatientId || backPatientId;
      navigate(
        patientIdForBack ? patientViewPath(fromPatientNhsId, patientIdForBack) : `/patient/${fromPatientNhsId}`,
        {
          state: {
            highlightDocumentId: documentId
          }
        }
      );
    } else if (fromConsultationId) {
      try {
        sessionStorage.removeItem(DOCUMENT_FROM_CONSULTATION_KEY);
      } catch (error) {
        console.warn('Failed to clear consultation context from session storage:', error);
      }

      navigate(`/consultation-detail/${fromConsultationId}`);
    } else if (location.state?.fromTask && location.state?.taskId) {
      // Fallback for old logic (shouldn't be needed but keeping for safety)
      navigate('/', {
        state: {
          selectTab: 'tasks',
          highlightTaskId: location.state.taskId
        }
      });
    } else {
      // Default: navigate back to documents list with highlighting
      navigate('/', { state: { highlightDocumentId: documentId } });
    }
  }, [fromWorkflowId, fromTaskId, fromPatientNhsId, fromPatientId, backPatientId, fromConsultationId, location.state, navigate, documentId]);

  const backLabel = fromWorkflowId
    ? 'Back to Workflow'
    : fromTaskId
      ? 'Back to Task'
      : fromPatientNhsId
        ? 'Back to Patient'
        : fromConsultationId
          ? 'Back to Consultation'
          : 'Back to Documents';

  return {
    navigationContext,
    handleNavigatePrev,
    handleNavigateNext,
    prevButtonAnimating,
    nextButtonAnimating,
    requestAutoNavigate,
    handleBackToDocumentsList,
    backLabel,
  };
};

export default useDocumentNavigation;
